import moment from "moment";
import {
  changedSecret,
  createSyncConfiguration,
  CreateSyncConfigurationInput,
  maskedSecret,
  MergeConfigInput,
  newSecret,
  OneRosterProviderConfig,
  OneRosterSyncConfigInput,
  SecretMapper,
  SyncConfigurationNode,
  SyncType,
  updateSyncConfiguration,
  UpdateSyncConfigurationInput,
  WondeProviderConfig,
  WondeSyncConfigInput,
  YearAttributeConfig,
  YearAttributeConfigInput,
} from "../../../utils/api/SyncApi";

import type {
  APIConfiguration,
  AttributesConfig,
  DataTotality,
  MergeConfig,
  ProviderData,
  SISProvider,
  SisVariant,
  Status,
  YearAttributeEnum,
} from "./SisIntegration";

// --- Legacy (SIS form) <-> sync-api v2 enum translation ---------------------
// The SIS forms speak the legacy api-dispatcher vocabulary; sync-api v2 uses its
// own enum values. Reads translate v2 -> legacy (so the forms prefill correctly on
// edit); writes translate legacy -> v2 (so saves are accepted). Each table below is
// the single source of truth with a derived reverse. Unknown values pass through.
const invert = (m: Record<string, string>): Record<string, string> => Object.fromEntries(Object.entries(m).map(([k, v]) => [v, k]));

const xlate = (m: Record<string, string>, value?: string): string | undefined => (value === undefined ? undefined : m[value] ?? value);

const INTEGRATION_TYPE_TO_V2: Record<string, string> = {
  Achieve: "ACHIEVE",
  Aeries: "AERIES",
  Aspen: "ASPEN",
  Aspire: "ASPIRE",
  Blackbaud: "BLACKBAUD",
  Chalkable_iNOW: "CHALKABLE_INOW",
  ClassLink: "CLASSLINK",
  Ellucian: "ELLUCIAN",
  eSchoolPlus: "ESCHOOLPLUS",
  "FACTS (RenWEB)": "FACTS_RENWEB",
  Focus: "FOCUS",
  "Frontline Progressbook": "FRONTLINE_PROGRESSBOOK",
  Genesis: "GENESIS",
  Gradelink: "GRADELINK",
  Harmony: "HARMONY",
  Illuminate_Education: "ILLUMINATE_EDUCATION",
  "Infinite Campus": "INFINITE_CAMPUS",
  InResonance: "INRESONANCE",
  JCampus: "JCAMPUS",
  JMC: "JMC",
  Lumen_Touch: "LUMEN_TOUCH",
  OneRoster: "ONEROSTER",
  Powerschool: "POWERSCHOOL",
  Rediker: "REDIKER",
  Sapphire: "SAPPHIRE",
  GG4L: "GG4L",
  Schoolrunner: "SCHOOLRUNNER",
  Skyward: "SKYWARD",
  Sycamore: "SYCAMORE",
  Synergy: "SYNERGY",
  TxEIS: "TXEIS",
  Tyler: "TYLER",
  Veracross: "VERACROSS",
  Vision: "VISION",
  unit4: "UNIT4",
  Other: "OTHER",
};
const INTEGRATION_TYPE_TO_LEGACY = invert(INTEGRATION_TYPE_TO_V2);

const WONDE_DOMAIN_TO_V2: Record<string, string> = {
  "api.wonde.com": "DEFAULT",
  "api-ap-southeast-2.wonde.com": "AP_SOUTHEAST_2",
  "api-af-south-1.wonde.com": "AF_SOUTH_1",
};
const WONDE_DOMAIN_TO_LEGACY = invert(WONDE_DOMAIN_TO_V2);

const WONDE_EMAIL_TO_V2: Record<string, string> = { work: "WORK", email: "EMAIL", primary: "PRIMARY", home: "HOME" };
const WONDE_EMAIL_TO_LEGACY = invert(WONDE_EMAIL_TO_V2);

const WONDE_USERNAME_TO_V2: Record<string, string> = {
  active_directory_username: "ACTIVE_DIRECTORY_USERNAME",
  mis_id: "MIS_ID",
  username: "USERNAME",
};
const WONDE_USERNAME_TO_LEGACY = invert(WONDE_USERNAME_TO_V2);

const WONDE_TEACHER_USERNAME_TO_V2: Record<string, string> = {
  "": "NONE",
  username: "USERNAME",
  active_directory_username: "ACTIVE_DIRECTORY_USERNAME",
};
const WONDE_TEACHER_USERNAME_TO_LEGACY = invert(WONDE_TEACHER_USERNAME_TO_V2);

const WONDE_CLASSNAME_TO_V2: Record<string, string> = {
  "class.subject.data.code - class.description": "SUBJECT_CODE_AND_DESCRIPTION",
  "class.code - class.description": "CLASS_CODE_AND_DESCRIPTION",
};
const WONDE_CLASSNAME_TO_LEGACY = invert(WONDE_CLASSNAME_TO_V2);

const WONDE_GUARDIAN_ROLE_TO_V2: Record<string, string> = {
  "Lives with Pupil": "LIVES_WITH_PUPIL",
  "Parental Responsibility": "PARENTAL_RESPONSIBILITY",
  Correspondence: "CORRESPONDENCE",
};
const WONDE_GUARDIAN_ROLE_TO_LEGACY = invert(WONDE_GUARDIAN_ROLE_TO_V2);

// A sync `state` from which the run has finished (vs NEVER_RUN / RUNNING).
const ENDED_STATES = new Set(["SUCCEEDED", "FAILED", "PARTIAL"]);

// States the table renders as a bad outcome. The legacy `Status` shape has no
// state field — it infers failure from `error` being non-empty — so a failed run
// has to carry some text or the table reads it as a success.
const FAILED_STATES = new Set(["FAILED", "PARTIAL"]);

// sync-api drops a blank status message rather than serving "", so a failed run
// can legitimately arrive with no message at all. Falling through to undefined
// would clear the failure flag and render the run as "Synced", so the state
// supplies the text when the message cannot.
export const DEFAULT_FAILURE_MESSAGE = "The sync failed without reporting a reason.";

const toUnix = (ts?: string | null): number | undefined => (ts ? moment.utc(ts).unix() : undefined);

/**
 * Derive the SIS table's legacy `status` badge directly from the list node's `sync`
 * summary
 */
export const mapSyncSummaryToStatus = (node: SyncConfigurationNode): Status => {
  const status: Status = { sync_id: node.id };
  const sync = node.sync;
  if (!sync || sync.state === "NEVER_RUN") return status;
  status.error = FAILED_STATES.has(sync.state) ? sync.message?.trim() || DEFAULT_FAILURE_MESSAGE : undefined;
  status.start_time = toUnix(sync.startedAt);
  status.end_time = ENDED_STATES.has(sync.state) ? toUnix(sync.finishedAt) : undefined;
  status.data_totality = (sync.state === "PARTIAL" ? "Partial" : "Full") as DataTotality;
  return status;
};

const mapMergeConfig = (node: SyncConfigurationNode): MergeConfig | undefined => {
  if (!node.merge) return undefined;
  const { priority, attachOnly, method } = node.merge;
  const merge: MergeConfig = { priority, attachOnly };
  if (method?.expression !== undefined) {
    merge.computed = { expression: method.expression };
  } else if (method?.stripped !== undefined) {
    merge.email = { stripped: method.stripped };
  }
  return merge;
};

const mapYearAttributes = (attrs?: YearAttributeConfig): AttributesConfig | undefined => {
  if (!attrs) return undefined;
  const mappings: Record<string, YearAttributeEnum> = {};
  (attrs.mappings ?? []).forEach((m) => {
    mappings[m.customerValue] = m.enumValue as YearAttributeEnum;
  });
  return { years: { enabled: attrs.enabled, mappings } };
};

/**
 * Map a SM-API sync configuration node (from `/api/syncs`) into the SISProvider
 * shape the SIS table + forms consume. Enum fields are translated v2 -> legacy so
 * the forms prefill with values their <Select> options understand.
 *
 * Secrets are never returned by SM-API, so `client_secret` / `access_token` render
 * as the masked placeholder when a credential is set (presence signal only); the
 * update mapper treats that placeholder as "unchanged".
 */
export const mapSyncNodeToSISProvider = (node: SyncConfigurationNode): SISProvider => {
  const isWonde = node.syncType === SyncType.Wonde;
  const oneroster = !isWonde ? (node.config as OneRosterProviderConfig | undefined) : undefined;
  const wonde = isWonde ? (node.config as WondeProviderConfig | undefined) : undefined;

  // SisVariant is a string enum; use its string values directly so this stays a
  // type-only dependency on SisIntegration.
  const variant = (
    isWonde
      ? "wonde"
      : oneroster?.variant === "CLASSLINK"
      ? "classlink"
      : oneroster?.variant === "POWERSCHOOL"
      ? "powerschool"
      : "oneroster"
  ) as SisVariant;

  let api_configuration: APIConfiguration = {};
  if (oneroster) {
    api_configuration = {
      client_id: oneroster.clientId,
      endpoint_url: oneroster.endpointUrl,
      token_url: oneroster.tokenUrl ?? undefined,
      client_secret: maskedSecret(oneroster.credential),
    };
  } else if (wonde) {
    api_configuration = {
      district_id: wonde.wondeDistrictID,
      domain: xlate(WONDE_DOMAIN_TO_LEGACY, wonde.domain),
      user_email_key: xlate(WONDE_EMAIL_TO_LEGACY, wonde.studentEmailField),
      email_key: xlate(WONDE_EMAIL_TO_LEGACY, wonde.teacherEmailField),
      username_key: xlate(WONDE_USERNAME_TO_LEGACY, wonde.studentUsernameField),
      teacher_username_key: xlate(WONDE_TEACHER_USERNAME_TO_LEGACY, wonde.teacherUsernameField),
      classname_keys: xlate(WONDE_CLASSNAME_TO_LEGACY, wonde.classNameFormat),
      guardian_email_key: xlate(WONDE_EMAIL_TO_LEGACY, wonde.guardianEmailField),
      guardian_roles: (wonde.guardianRoles ?? []).map((r) => xlate(WONDE_GUARDIAN_ROLE_TO_LEGACY, r) as string),
      access_token: maskedSecret(wonde.credential),
    };
  }

  return {
    id: node.id,
    type: variant,
    name: node.name,
    enabled: node.enabled,
    // Wonde pins integration_type to "Other" in the form; OneRoster translates v2 -> legacy.
    integration_type: isWonde ? "Other" : xlate(INTEGRATION_TYPE_TO_LEGACY, oneroster?.integrationType) ?? "",
    sync_classrooms: oneroster?.syncClassrooms ?? wonde?.syncClassrooms ?? false,
    sync_parents: oneroster?.syncParents ?? wonde?.syncParents ?? false,
    respect_enrolment_date: oneroster?.respectEnrolmentDate ?? false,
    respect_sessions: oneroster?.respectAcademicSessions ?? false,
    compatibility_mode: oneroster?.compatibilityMode,
    excluded_schools_list: oneroster?.excludedSchools,
    // Form parent-type options are capitalised on load and lowercased on save.
    sync_parent_types: oneroster?.onerosterSyncParentTypes?.map((t) => t.toLowerCase()),
    api_configuration,
    merge_config: mapMergeConfig(node),
    sync_attributes: mapYearAttributes(oneroster?.yearAttributes ?? wonde?.yearAttributes),
    // Status derived from the list summary so the SM-API path needs no executions call.
    status: mapSyncSummaryToStatus(node),
    // Non-null means sync-api served this row degraded (config/merge null); the UI
    // must flag it rather than present the form defaults as if they were real config.
    read_error: node.readError ?? undefined,
  };
};

// --- Write path: SISProvider (legacy form payload) -> sync-api v2 input -----

const buildMerge = (mc?: MergeConfig): MergeConfigInput => {
  const method = mc?.computed
    ? { computed: { expression: mc.computed.expression } }
    : { email: { stripped: mc?.email?.stripped ?? false } };
  return { priority: mc?.priority ?? 0, attachOnly: mc?.attachOnly ?? false, method };
};

const buildYearAttributesInput = (ac?: AttributesConfig): YearAttributeConfigInput | undefined => {
  const years = ac?.years;
  if (!years) return undefined;
  return {
    enabled: years.enabled,
    mappings: Object.entries(years.mappings).map(([customerValue, enumValue]) => ({ customerValue, enumValue })),
  };
};

// sync-api reads `tokenUrl` as a three-way choice: absent selects OAuth 1.0a,
// present selects OAuth 2, and present-but-empty is rejected because it selects
// OAuth 2 with nowhere to fetch a token from. The form has no such distinction —
// an untouched OAuth2 Token URL field is "", the same value as one the user
// cleared — so a blank field has to be omitted rather than sent, which is also
// what the legacy CMS migration does with it.
const toOptionalUrl = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const buildOnerosterInput = (data: ProviderData, secret: SecretMapper): OneRosterSyncConfigInput => ({
  variant: data.type.toUpperCase(),
  integrationType: xlate(INTEGRATION_TYPE_TO_V2, data.integration_type),
  endpointUrl: data.api_configuration.endpoint_url,
  tokenUrl: toOptionalUrl(data.api_configuration.token_url),
  clientId: data.api_configuration.client_id,
  clientSecret: secret(data.api_configuration.client_secret),
  syncClassrooms: data.sync_classrooms,
  syncParents: data.sync_parents,
  syncParentTypes: (data.sync_parent_types ?? []).map((t) => t.toUpperCase()),
  respectEnrolmentDate: data.respect_enrolment_date,
  respectAcademicSessions: data.respect_sessions,
  compatibilityMode: data.compatibility_mode,
  yearAttributes: buildYearAttributesInput(data.sync_attributes),
  excludedSchools: data.excluded_schools_list,
});

const buildWondeInput = (data: ProviderData, secret: SecretMapper): WondeSyncConfigInput => ({
  districtID: data.api_configuration.district_id,
  domain: xlate(WONDE_DOMAIN_TO_V2, data.api_configuration.domain),
  accessToken: secret(data.api_configuration.access_token),
  studentEmailField: xlate(WONDE_EMAIL_TO_V2, data.api_configuration.user_email_key),
  studentUsernameField: xlate(WONDE_USERNAME_TO_V2, data.api_configuration.username_key),
  classNameFormat: xlate(WONDE_CLASSNAME_TO_V2, data.api_configuration.classname_keys),
  teacherUsernameField: xlate(WONDE_TEACHER_USERNAME_TO_V2, data.api_configuration.teacher_username_key),
  teacherEmailField: xlate(WONDE_EMAIL_TO_V2, data.api_configuration.email_key),
  guardianEmailField: xlate(WONDE_EMAIL_TO_V2, data.api_configuration.guardian_email_key),
  guardianRoles: (data.api_configuration.guardian_roles ?? []).map((r) => xlate(WONDE_GUARDIAN_ROLE_TO_V2, r) as string),
  syncClassrooms: data.sync_classrooms,
  syncParents: data.sync_parents,
  yearAttributes: buildYearAttributesInput(data.sync_attributes),
});

export const mapProviderDataToCreateInput = (data: ProviderData): CreateSyncConfigurationInput => {
  const input: CreateSyncConfigurationInput = {
    syncType: data.type.toUpperCase() as SyncType,
    name: data.name,
    enabled: data.enabled,
    merge: buildMerge(data.merge_config),
  };
  if (data.type === "wonde") input.wonde = buildWondeInput(data, newSecret);
  else input.oneroster = buildOnerosterInput(data, newSecret);
  return input;
};

export const mapProviderDataToUpdateInput = (data: ProviderData): UpdateSyncConfigurationInput => {
  const input: UpdateSyncConfigurationInput = {
    name: data.name,
    enabled: data.enabled,
    merge: buildMerge(data.merge_config),
  };
  if (data.type === "wonde") input.wonde = buildWondeInput(data, changedSecret);
  else input.oneroster = buildOnerosterInput(data, changedSecret);
  return input;
};

/** Create (no id) or update (id) a SIS sync via SM-API, mapping the legacy form payload. */
export const saveSisSync = async (data: ProviderData, id?: string): Promise<void> => {
  if (id) {
    await updateSyncConfiguration(id, mapProviderDataToUpdateInput(data));
  } else {
    await createSyncConfiguration(mapProviderDataToCreateInput(data));
  }
};
